import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Icon, Markdown, PageHeader } from '@carrent/ui'
import { kbApi } from '@carrent/api'
import { useKbPage, useKbTree } from '@carrent/domain'
import { ArticleList } from './KbSpacePage'
import { cachedArticle } from '../../../offline/knowledge'

const resolveImage = (href) => {
  const m = String(href || '').match(/^attachment:(\d+)$/)
  if (m) return kbApi.attachmentUrl(m[1])
  return /^(https?:|\/)/i.test(href) ? href : null
}

/**
 * Инструкция на телефоне.
 *
 * Текст занимает весь экран: водитель читает его на ходу, часто одной
 * рукой. Вложенные статьи показаны списком в конце — так раздел
 * раскрывается без деревьев и треугольников.
 */
export default function KbArticlePage() {
  const { spaceKey, pageId } = useParams()
  const navigate = useNavigate()
  const { page, loading, error } = useKbPage(Number(pageId))
  const { pages } = useKbTree(spaceKey)

  // Закреплённые инструкции лежат на устройстве: в гараже без сети
  // порядок действий при ДТП должен открыться, а не показать ошибку.
  const [offlineCopy, setOfflineCopy] = useState(null)
  useEffect(() => {
    if (error) setOfflineCopy(cachedArticle(pageId))
    else setOfflineCopy(null)
  }, [error, pageId])

  const children = useMemo(
    () => pages.filter((p) => p.parent_id === Number(pageId)),
    [pages, pageId],
  )

  const parent = page && pages.find((p) => p.id === page.parent_id)
  const backTo = parent ? `/kb/${spaceKey}/${parent.id}` : `/kb/${spaceKey}`

  const attachments = page?.attachments || []

  return (
    <div className="p-page flex-col gap">
      <PageHeader title={page?.title || 'Статья'} onBack={() => navigate(backTo)} />

      {loading && <div className="kb-m__loading"><div className="spinner" /></div>}

      {error && offlineCopy && (
        <>
          <p className="kb-m__offline">
            <Icon name="warning" size={15} aria-hidden="true" />
            Показываем сохранённую копию — сети сейчас нет
          </p>
          <Markdown
            text={offlineCopy.body_md}
            resolveImage={resolveImage}
            className="kb-m__body"
          />
        </>
      )}

      {error && !offlineCopy && (
        <div className="kb-m__state">
          <Icon name="warning" size={28} aria-hidden="true" />
          <p className="kb-m__state-title">{error}</p>
          <p className="kb-m__state-hint">
            Возможно, статью убрали в архив или пропала связь.
          </p>
        </div>
      )}

      {page && !loading && (
        <>
          {page.pinned && (
            <p className="kb-m__pinned">
              <Icon name="check" size={15} aria-hidden="true" />
              Важная инструкция
            </p>
          )}

          <Markdown
            text={page.body_md}
            resolveImage={resolveImage}
            className="kb-m__body"
          />

          {attachments.length > 0 && (
            <section>
              <h2 className="kb-m__section">Файлы</h2>
              <nav className="more-list" aria-label="Вложения">
                {attachments.map((a) => (
                  <a
                    key={a.id}
                    className="more-list__item"
                    href={kbApi.attachmentUrl(a.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon name="file" size={22} className="more-list__icon" />
                    <span className="more-list__text">
                      <span className="more-list__label">{a.filename}</span>
                    </span>
                    <Icon name="chevronRight" size={18} className="more-list__chevron" />
                  </a>
                ))}
              </nav>
            </section>
          )}

          {children.length > 0 && (
            <section>
              <h2 className="kb-m__section">В этом разделе</h2>
              <ArticleList pages={children} spaceKey={spaceKey} />
            </section>
          )}
        </>
      )}
    </div>
  )
}
